import { invalidNameParts } from '@/app_config/qual_config';
import { states } from '@/app_config';
import { intersection, isEmpty, head, hasOwnProperty } from './helpers';
import { calculateAge, isValidDate } from './utils';
import { TenureItem } from '@/interfaces/app_config';
import { AGE_LIMIT } from './app_consts';

export interface ErrorBag {
  [key: string]: any;
}

interface RulesBag {
  [id: string]: any;
}

const generateChecksum = (arr: number[]) => {
  let checksum = 0;
  // The multiplication table
  const d = [
    [0, 1, 2, 3, 4, 5, 6, 7, 8, 9],
    [1, 2, 3, 4, 0, 6, 7, 8, 9, 5],
    [2, 3, 4, 0, 1, 7, 8, 9, 5, 6],
    [3, 4, 0, 1, 2, 8, 9, 5, 6, 7],
    [4, 0, 1, 2, 3, 9, 5, 6, 7, 8],
    [5, 9, 8, 7, 6, 0, 4, 3, 2, 1],
    [6, 5, 9, 8, 7, 1, 0, 4, 3, 2],
    [7, 6, 5, 9, 8, 2, 1, 0, 4, 3],
    [8, 7, 6, 5, 9, 3, 2, 1, 0, 4],
    [9, 8, 7, 6, 5, 4, 3, 2, 1, 0],
  ];
  // permutation table p
  const p = [
    [0, 1, 2, 3, 4, 5, 6, 7, 8, 9],
    [1, 5, 7, 6, 2, 8, 3, 0, 9, 4],
    [5, 8, 0, 3, 7, 9, 6, 1, 4, 2],
    [8, 9, 1, 6, 0, 4, 3, 5, 2, 7],
    [9, 4, 5, 3, 1, 2, 6, 8, 7, 0],
    [4, 2, 8, 6, 5, 7, 3, 9, 0, 1],
    [2, 7, 9, 3, 8, 0, 6, 4, 1, 5],
    [7, 0, 4, 6, 9, 1, 3, 2, 5, 8],
  ];
  // inverse table inv
  const inv = [0, 4, 3, 2, 1, 5, 6, 7, 8, 9];
  const invertedArray = arr.reverse();
  for (let idx = 0; idx < invertedArray.length; idx++) {
    checksum = d[checksum][p[(idx + 1) % 8][invertedArray[idx]]];
  }
  return inv[checksum];
};

const rulesRegex: RulesBag = {
  alpha: {
    type: 'regex',
    matchPositive: true,
    regex: /^[A-Za-z]+$/,
    message(fieldName: string) {
      return `The ${fieldName} field can contain alphabets only.`;
    },
  },
  alpha_num: {
    type: 'regex',
    matchPositive: true,
    regex: /^[A-Za-z0-9]+$/,
    message(fieldName: string) {
      return `The ${fieldName} field can contain alphabets and numbers only.`;
    },
  },
  alpha_spaces: {
    type: 'regex',
    matchPositive: true,
    regex: /^[a-zA-Z\s]*$/,
    message(fieldName: string) {
      return `The ${fieldName} field can contain letters and spaces only.`;
    },
  },
  name: {
    type: 'regex',
    matchPositive: true,
    regex: /^[A-Za-z]{1}[A-Za-z\s]*$/,
    message(fieldName: string) {
      return `The ${fieldName} field can contain alphabets and spaces only.`;
    },
  },
  email: {
    type: 'regex',
    matchPositive: true,
    regex:
      /^([0-9a-zA-Z]([-.\w]*[0-9a-zA-Z])*@([0-9a-zA-Z][-\w]*[0-9a-zA-Z]\.)+[a-zA-Z]{2,9})$/,
    message(fieldName: string) {
      return `The ${fieldName} field is not in valid email format.`;
    },
  },
  float: {
    type: 'regex',
    matchPositive: true,
    regex: /^[0-9]{7}$/,
    message(fieldName: string) {
      return `The ${fieldName} field can contain floating point numbers only.`;
    },
  },
  integer: {
    type: 'regex',
    matchPositive: true,
    regex: /^[0-9]+$/,
    message(fieldName: string) {
      return `The ${fieldName} field can contain numeric only.`;
    },
  },
  address: {
    type: 'regex',
    matchPositive: true,
    regex: /^([a-zA-Z0-9 ]|[./,-])+$/,
    message(fieldName: string) {
      return `The ${fieldName} field cannot contain any special characters other than . - and ,`;
    },
  },
  repeatChars: {
    type: 'regex',
    matchPositive: false,
    regex: /([^0-9])\1{2,}/,
    message(fieldName: string) {
      return `The ${fieldName} field does not allow three repeated characters`;
    },
  },
  noEscChars: {
    type: 'regex',
    matchPositive: false,
    regex: /\\[a-zA-Z0-9"'\\]/,
    message(fieldName: string) {
      return `The ${fieldName} field does not allow escape characters`;
    },
  },
  required: {
    type: 'regex',
    matchPositive: true,
    regex: /.+/,
    message(fieldName: string) {
      return `The ${fieldName} field is required.`;
    },
  },
  inviteCode: {
    type: 'regex',
    matchPositive: true,
    regex: /^MT[A-Z1-9]{2}Q[A-Z1-9]{3}$/,
    message() {
      return 'Please enter valid invite code.';
    },
  },
  min: {
    type: 'dynamic-regex',
    matchPositive: true,
    limit: '0',
    regex(val: string) {
      this.limit = val;
      return new RegExp(`^.{${val},}$`);
    },
    message(fieldName: string) {
      return `The ${fieldName} field should be of minimum ${this.limit} characters.`;
    },
  },
  max: {
    type: 'dynamic-regex',
    matchPositive: true,
    limit: '0',
    regex(val: string) {
      this.limit = val;
      return new RegExp(`^.{0,${val}}$`);
    },
    message(fieldName: string) {
      return `The ${fieldName} field should not exceed a maximum of ${this.limit} characters.`;
    },
  },
  limit: {
    type: 'dynamic-regex',
    matchPositive: true,
    minimum: '0',
    maximum: '0',
    regex(limit: string) {
      const [min, max] = limit.split(',');
      this.minimum = min;
      this.maximum = max;
      return new RegExp(`^.{${min},${max}}$`);
    },
    message(fieldName: string) {
      return `The ${fieldName} field should be between ${this.minimum} & ${this.maximum} characters.`;
    },
  },
  validName: {
    type: 'validator',
    validate(item: any) {
      if (item.value) {
        const nameParts = item.value.split(' ');
        const intersectedArray = intersection(
          nameParts.map((v: any) => v.toLowerCase()),
          invalidNameParts
        );
        if (!isEmpty(intersectedArray)) {
          return false;
        }
      }
      return true;
    },
    message(fieldName: string) {
      return `The ${fieldName} field is incorrect.`;
    },
  },
  isNumber: {
    type: 'validator',
    validate(item: any) {
      const monthlyIncome = Number(item.value);
      if (isNaN(monthlyIncome)) {
        return false;
      }
      return true;
    },
    message(fieldName: string) {
      return `The ${fieldName} field should be a number.`;
    },
  },
  minValue: {
    type: 'validator',
    minValue: 0,
    validate(item: any) {
      this.minValue = item.minimum;
      if (!isNaN(item.value)) {
        if (item.value < item.minimum) {
          return false;
        }
        return true;
      }
      return false;
    },
    message(fieldName: string) {
      return `The ${fieldName} field should be greater than ${this.minValue}.`;
    },
  },
  maxValue: {
    type: 'validator',
    maxValue: 0,
    validate(item: any) {
      this.maxValue = item.maximum;
      if (!isNaN(item.value)) {
        if (item.value > item.maximum) {
          return false;
        }
        return true;
      }
      return false;
    },
    message(fieldName: string) {
      return `The ${fieldName} field should be lesser than ${this.maxValue}.`;
    },
  },
  noSpecialChars: {
    type: 'regex',
    matchPositive: true,
    regex: /^[^*|":<>[\]{}`\\()';@&$#]+$/,
    message(fieldName: string) {
      return `The ${fieldName} field cannot contain special characters`;
    },
  },
  companyName: {
    type: 'regex',
    matchPositive: true,
    regex: /^[^|":<>[\]{}`\\;@$#]+$/,
    message(fieldName: string) {
      return `The ${fieldName} field cannot contain special characters`;
    },
  },
  pancard: {
    type: 'regex',
    matchPositive: true,
    regex: /^[A-Za-z]{3}[PCHFATBLJGpchfatbljg][\w][\d]{4}[A-Za-z]$/,
    message() {
      return 'Invalid PAN';
    },
  },
  validCity: {
    type: 'validator',
    validate(item: any) {
      const cities = item.cities.map((city: any) => city.value);
      if (
        item.value &&
        (!cities.includes(item.value) ||
          ['OTHERS', 'others'].includes(item.value))
      ) {
        return false;
      }
      return true;
    },
    message() {
      return 'We currently serve the above mentioned cities';
    },
  },
  validBank: {
    type: 'validator',
    validate(item: any) {
      const bankNames = item.bankNames;
      if (item.value && !bankNames.includes(item.value)) {
        return false;
      }
      return true;
    },
    message() {
      return 'Please select a bank from the list';
    },
  },
  validJobType: {
    type: 'validator',
    validate(item: any) {
      if (item.value && ['OTHER', 'other'].includes(item.value)) {
        return false;
      }
      return true;
    },
    message() {
      return 'We currently serve the above mentioned job types';
    },
  },
  validAge: {
    type: 'validator',
    validate(item: any) {
      const age = calculateAge(item.value);
      if (!age) {
        return false;
      }
      if (age < AGE_LIMIT.MIN || age > AGE_LIMIT.MAX) {
        return false;
      }
      return true;
    },
    message() {
      return `You should be between ${AGE_LIMIT.MIN} to ${AGE_LIMIT.MAX} years to apply`;
    },
  },
  validDate: {
    type: 'validator',
    validate(item: any) {
      if (item.value) {
        return isValidDate(item.value);
      }
    },
    message() {
      return 'Invalid Date';
    },
  },
  validTenure: {
    type: 'validator',
    validate(item: any) {
      return isValidTenure(item.currentTenure, item.totalTenure, item.tenures);
    },
    message() {
      return 'Not a valid duration';
    },
  },
  validPincode: {
    type: 'validator',
    validate(item: any) {
      return isValidPincode(item.state, item.value);
    },
    message() {
      return 'Invalid Pincode';
    },
  },
  range: {
    type: 'validator',
    validate(item: any) {
      this.item = item;
      this.income = Number(item.value);
      if (
        (!isNaN(this.income) && this.income < item.minimum) ||
        this.income > item.maximum
      ) {
        return false;
      }
      return true;
    },
    message() {
      if (isNaN(this.income)) {
        return 'Monthly take home salary should be a number';
      } else if (this.income < this.item.minimum) {
        return `Monthly take home salary should be greater than Rs ${this.item.minimum}`;
      } else if (this.income > this.item.maximum) {
        return `You are kidding me, right?`;
      }
    },
  },
  validAadhaar: {
    type: 'validator',
    validate(item: any) {
      const aadhaar = item.value;
      const aadhaarArr = aadhaar
        .split('')
        .map((value: string) => parseInt(value, 10));
      const checkSumChecker = aadhaarArr.pop();
      if (generateChecksum(aadhaarArr) === parseInt(checkSumChecker, 10)) {
        return true;
      }
      return false;
    },
    message() {
      return 'Invalid aadhaar. Please enter a valid aadhaar number';
    },
  },
};

export const validateAll = (config: any): ErrorBag => {
  return new Promise((resolve) => {
    const errors: ErrorBag = {};
    // Looping for each field
    for (const item of config) {
      errors[item.id] = validate(item);
    }

    // Checking if Errors exists
    for (const error of Object.keys(errors)) {
      if (errors[error].length !== 0) {
        resolve(errors);
      }
    }
    resolve(false);
  });
};

export const validate = (item: any) => {
  let errors: string[] = [];

  if (hasOwnProperty(item, 'rules')) {
    const rules = item.rules.split('|');
    if (rules.includes('required')) {
      errors = checkForSetOfRules(rules, item);
    } else if (item.value) {
      errors = checkForSetOfRules(rules, item);
    }
  }
  return errors;
};

const checkForSetOfRules = (rules: string[], item: any) => {
  const errors: string[] = [];
  for (let rule of rules) {
    let currentRule;
    if (rule === 'confirmed') {
      if (item.value !== item.confirmValue) {
        //  check if field has custom messages
        if (
          hasOwnProperty(item, 'messages') &&
          hasOwnProperty(item.messages, rule)
        ) {
          errors.push(item.messages[rule]);
        } else {
          // apply default message
          errors.push(`Values do not match`);
        }
      }
      continue;
    } else if (rule.includes(':')) {
      const [ruleName] = rule.split(':');
      currentRule = rulesRegex[ruleName];
    } else {
      currentRule = rulesRegex[rule];
    }
    //  Checking for each rule
    let testPassed: boolean = false;
    if (currentRule) {
      let computedRegex: RegExp = /^.*$/;
      if (currentRule.type === 'regex') {
        computedRegex = currentRule.regex;
      } else if (currentRule.type === 'dynamic-regex') {
        const [ruleName, limit] = rule.split(':');
        computedRegex = currentRule.regex(limit);
        rule = ruleName;
      }

      // For Boolean values
      if (typeof item.value === 'boolean') {
        testPassed = item.value;
      } else if (['regex', 'dynamic-regex'].includes(currentRule.type)) {
        // for other than boolean
        testPassed =
          computedRegex.test(item.value) === currentRule.matchPositive;
      } else if (currentRule.type === 'validator') {
        testPassed = currentRule.validate(item);
      }

      if (!testPassed) {
        // check if field has custom messages
        if (
          hasOwnProperty(item, 'messages') &&
          hasOwnProperty(item.messages, rule)
        ) {
          errors.push(item.messages[rule]);
        } else {
          // apply default message
          errors.push(currentRule.message(item.name || item.id));
        }
      }
    }
  }

  if (hasOwnProperty(item, 'regex')) {
    if (!item.regex.test(item.value)) {
      // check if field has custom messages
      if (
        hasOwnProperty(item, 'messages') &&
        hasOwnProperty(item.messages, 'regex')
      ) {
        errors.push(item.messages.regex);
      } else {
        // apply default message
        errors.push(
          `The ${item.name || item.id} field is not in valid format.`
        );
      }
    }
  }
  return errors;
};

const isValidTenure = (
  currentTenure: string,
  totalTenure: string,
  tenures: TenureItem[]
) => {
  if (currentTenure && totalTenure) {
    const current = head(
      tenures.filter((tenureItem: any) => tenureItem.value === currentTenure)
    );
    const total = head(
      tenures.filter((tenureItem: any) => tenureItem.value === totalTenure)
    );
    if (current && total) {
      const currentInMonths = current.tenure.years * 12 + current.tenure.months;
      const totalInMonths = total.tenure.years * 12 + total.tenure.months;
      if (totalInMonths < currentInMonths) {
        return false;
      }
      return true;
    } else {
      return false;
    }
  }
  return true;
};

const isValidPincode = (selectedState: string, pincode: any) => {
  const state = states.find((item: any) => item.name === selectedState);
  if (state && pincode && pincode.toString().length === 6) {
    const pincodeStr = pincode.toString();
    const pincodePrefix = parseInt(pincodeStr.substring(0, 2), 10);
    if (pincodePrefix >= state.min && pincodePrefix <= state.max) {
      return true;
    }
  }
  return false;
};
